import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Helmet } from "react-helmet-async";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { useGetOpportunityQuery } from "@/state/opportunitiesApi";
import { useSubmitExpressInterestMutation } from "@/state/submissionsApi";

const ExpressInterestSchema = z.object({
  name: z.string().min(1, "Full name is required"),
  email: z.email("Invalid email address"),
  company: z.string().optional(),
  phone: z.string().optional(),
  message: z.string().max(500).optional(),
});

type ExpressInterestValues = z.infer<typeof ExpressInterestSchema>;

export default function OpportunityDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { data, isLoading, isError } = useGetOpportunityQuery(id!);
  const [submitInterest, { isLoading: submitting }] = useSubmitExpressInterestMutation();
  const opp = data?.data;
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const { register, handleSubmit, formState: { errors }, watch, reset } = useForm<ExpressInterestValues>({
    resolver: zodResolver(ExpressInterestSchema),
    defaultValues: { name: "", email: "", company: "", phone: "", message: "" },
  });

  const messageLength = watch("message")?.length ?? 0;

  const onSubmit = async (data: ExpressInterestValues) => {
    setSubmitError("");
    try {
      await submitInterest({
        opportunityId: opp?.id,
        opportunityTitle: opp?.title ?? "",
        companyName: data.company || "",
        contactName: data.name,
        email: data.email,
        phone: data.phone || undefined,
        message: data.message || undefined,
      }).unwrap();
      setSubmitted(true);
      reset();
    } catch (err) {
      const message = (err as any)?.data?.error?.message;
      setSubmitError(message ?? "Something went wrong. Please try again.");
    }
  };

  if (isLoading) {
    return (
      <main>
        <Navbar />
        <div className="min-h-[60vh] flex items-center justify-center">
          <i className="ri-loader-4-line animate-spin text-3xl text-[#00C2C2]" />
        </div>
        <Footer />
      </main>
    );
  }

  if (isError || !opp) {
    return (
      <main>
        <Navbar />
        <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-6">
          <div className="w-14 h-14 flex items-center justify-center rounded-full bg-gray-100 mb-4 text-2xl">
            <i className="ri-search-line text-gray-400" />
          </div>
          <h2 className="text-2xl font-bold text-[#0A1628] mb-2">Opportunity Not Found</h2>
          <p className="text-gray-500 text-sm mb-6">This opportunity may have been filled or removed.</p>
          <Link to="/business#opportunities" className="bg-[#0A1628] text-white px-6 py-2.5 rounded-full text-sm font-semibold hover:bg-[#0A1628] transition-colors whitespace-nowrap cursor-pointer">
            Browse All Opportunities
          </Link>
        </div>
        <Footer />
      </main>
    );
  }

  return (
    <main>
      <Helmet>
        <title>{opp.title} | Investment Opportunity in {opp.country} | AIP</title>
        <meta name="description" content={`${opp.description?.slice(0, 155) ?? `Explore this ${opp.sector} investment opportunity in ${opp.country}. Connect with Africa Investment Portal to learn more.`}`} />
        <link rel="canonical" href={`https://africainvestmentportal.com/business/opportunity/${opp.id}`} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={`${opp.title} | Investment Opportunity in ${opp.country}`} />
        <meta property="og:description" content={opp.description?.slice(0, 155) ?? `${opp.sector} investment opportunity in ${opp.country}.`} />
        <meta property="og:url" content={`https://africainvestmentportal.com/business/opportunity/${opp.id}`} />
        {opp.image && <meta property="og:image" content={opp.image} />}
      </Helmet>
      <Navbar />

      <section className="relative pt-32 pb-0 bg-[#0A1628] overflow-hidden">
        <div className="absolute inset-0">
          {opp.image && (
            <img src={opp.image} alt={opp.title} className="w-full h-full object-cover object-top opacity-25" />
          )}
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A1628]/95 to-[#0A1628]/60" />
        </div>
        <div className="relative z-10 w-full px-6 lg:px-12 pb-16 pt-4">
          <Link to="/business#opportunities" className="inline-flex items-center gap-2 text-white/50 text-sm hover:text-white transition-colors mb-8 cursor-pointer whitespace-nowrap">
            <i className="ri-arrow-left-line" /> Back to Opportunities
          </Link>
          <div className="flex flex-wrap gap-2 mb-4">
            <span className="text-xs font-semibold px-3 py-1 rounded-full bg-[#0A1628] text-white">{opp.sector}</span>
            <span className="text-xs font-semibold px-3 py-1 rounded-full bg-white/10 text-white/70 flex items-center gap-1">
              <i className="ri-map-pin-2-line text-xs" /> {opp.country}
            </span>
          </div>
          <h1 className="text-3xl md:text-5xl font-bold text-white max-w-3xl leading-tight mb-4">{opp.title}</h1>
          {opp.investmentLevel && (
            <div className="flex items-center gap-2 text-white/60 text-sm">
              <i className="ri-funds-line text-[#00C2C2]" />
              <span>Investment Level: <strong className="text-white">{opp.investmentLevel}</strong></span>
            </div>
          )}
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="w-full px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2">
              {opp.image && (
                <img src={opp.image} alt={opp.title} className="w-full h-64 object-cover object-top rounded-lg mb-8" />
              )}
              <h2 className="text-xl font-bold text-[#0A1628] mb-4">About this Opportunity</h2>
              <p className="text-gray-600 leading-relaxed mb-8">{opp.description}</p>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-8">
                {[
                  { icon: "ri-map-pin-2-line", label: "Country", value: opp.country },
                  { icon: "ri-building-line", label: "Sector", value: opp.sector },
                  { icon: "ri-funds-line", label: "Investment", value: opp.investmentLevel || "Open to Discuss" },
                ].map((item) => (
                  <div key={item.label} className="bg-[#f8f9fc] rounded-lg p-4 border border-gray-100">
                    <div className="w-7 h-7 flex items-center justify-center text-[#00C2C2] mb-2"><i className={`${item.icon} text-base`} /></div>
                    <p className="text-xs text-gray-400 uppercase tracking-wide font-semibold mb-1">{item.label}</p>
                    <p className="text-sm font-bold text-[#0A1628]">{item.value}</p>
                  </div>
                ))}
              </div>

              <div className="flex flex-wrap gap-2">
                {opp.tags.map((tag) => (
                  <span key={tag} className="text-xs font-semibold px-3 py-1.5 rounded-full bg-[#0A1628]/10 text-[#00C2C2]">{tag}</span>
                ))}
              </div>
            </div>

            <div className="lg:col-span-1">
              <div className="bg-[#f8f9fc] rounded-lg border border-gray-200 p-6 sticky top-24">
                <h3 className="text-base font-bold text-[#0A1628] mb-1">Express Interest</h3>
                <p className="text-xs text-gray-500 mb-6">Fill in the form and our team will connect with you within 24–48 hours.</p>

                {submitted ? (
                  <div className="text-center py-8">
                    <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#0A1628] text-white text-xl mx-auto mb-3">
                      <i className="ri-check-line" />
                    </div>
                    <p className="font-bold text-[#0A1628] text-sm mb-1">Interest Submitted!</p>
                    <p className="text-xs text-gray-500">We&apos;ll be in touch shortly.</p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
                    {submitError && (
                      <p className="text-xs text-red-500 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{submitError}</p>
                    )}
                    <div className="flex flex-col gap-1.5">
                      <label className="text-xs font-semibold text-[#0A1628] uppercase tracking-wide">Full Name *</label>
                      <input type="text" placeholder="Jane Doe" {...register("name")} className="text-sm border border-gray-200 rounded-lg px-3 py-2.5 bg-white focus:outline-none focus:border-[#00C2C2] text-[#0A1628] placeholder-gray-400 transition-colors" />
                      {errors.name && <p className="text-xs text-red-500">{errors.name.message}</p>}
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <label className="text-xs font-semibold text-[#0A1628] uppercase tracking-wide">Email *</label>
                      <input type="email" placeholder="jane@company.com" {...register("email")} className="text-sm border border-gray-200 rounded-lg px-3 py-2.5 bg-white focus:outline-none focus:border-[#00C2C2] text-[#0A1628] placeholder-gray-400 transition-colors" />
                      {errors.email && <p className="text-xs text-red-500">{errors.email.message}</p>}
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <label className="text-xs font-semibold text-[#0A1628] uppercase tracking-wide">Company</label>
                      <input type="text" placeholder="Company name" {...register("company")} className="text-sm border border-gray-200 rounded-lg px-3 py-2.5 bg-white focus:outline-none focus:border-[#00C2C2] text-[#0A1628] placeholder-gray-400 transition-colors" />
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <label className="text-xs font-semibold text-[#0A1628] uppercase tracking-wide">Phone</label>
                      <input type="tel" placeholder="+250 7XX XXX XXX" {...register("phone")} className="text-sm border border-gray-200 rounded-lg px-3 py-2.5 bg-white focus:outline-none focus:border-[#00C2C2] text-[#0A1628] placeholder-gray-400 transition-colors" />
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <label className="text-xs font-semibold text-[#0A1628] uppercase tracking-wide">Message</label>
                      <textarea rows={3} maxLength={500} placeholder="Tell us about your interest..." {...register("message")} className="text-sm border border-gray-200 rounded-lg px-3 py-2.5 bg-white focus:outline-none focus:border-[#00C2C2] text-[#0A1628] placeholder-gray-400 transition-colors resize-none" />
                      <p className="text-xs text-gray-400 text-right">{messageLength}/500</p>
                    </div>
                    <button type="submit" disabled={submitting} className="bg-[#0A1628] text-white text-sm font-bold py-3 rounded-lg hover:bg-[#0A1628] transition-colors whitespace-nowrap disabled:opacity-60 cursor-pointer flex items-center justify-center gap-2">
                      {submitting ? <><i className="ri-loader-4-line animate-spin" /> Sending...</> : <><i className="ri-send-plane-line" /> Express Interest</>}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
